// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button from "@awc/MobiusOverrides/Button/Button";
import DataTable, { DataTableProps } from "@awc/MobiusOverrides/DataTable/DataTable";
import DataTableBody from "@awc/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell from "@awc/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead from "@awc/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader from "@awc/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow from "@awc/MobiusOverrides/DataTable/DataTableRow";
import TextField, { TextFieldProps } from "@awc/MobiusOverrides/TextField";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import styled from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ReelCapacityCalculatorStyles {
    diameterTextField?: TextFieldProps;
    resultsWrapper?: InjectableCss;
    resultsText?: TypographyPresentationProps;
    diameterText?: TypographyPresentationProps;
    infoText?: TypographyPresentationProps;
    resultsTable?: DataTableProps;
}

export const reelCapacityCalculatorStyles: ReelCapacityCalculatorStyles = {};

const styles = reelCapacityCalculatorStyles;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
`;

interface Reel {
    shippingReel: string;
    factor: number;
    length: number;
    lengthMeters: number;
    weightPounds: number;
    weightKilos: number;
}

const initialReelArray = [
    {
        shippingReel: translate('Plastic 10.5" x 7"'),
        factor: 31.52,
        length: 0,
        lengthMeters: 0,
        weightPounds: 1,
        weightKilos: 0.45,
    },
    {
        shippingReel: translate('Plastic 12" x 7"'),
        factor: 44.02,
        length: 0,
        lengthMeters: 0,
        weightPounds: 2,
        weightKilos: 0.9,
    },
    {
        shippingReel: translate('Plywood 16" x 8.75"'),
        factor: 95.94,
        length: 0,
        lengthMeters: 0,
        weightPounds: 8,
        weightKilos: 3.6,
    },
    {
        shippingReel: translate('Plywood 24" x 12.25"'),
        factor: 290.66,
        length: 0,
        lengthMeters: 0,
        weightPounds: 15,
        weightKilos: 6.75,
    },
    {
        shippingReel: translate('Wood 36" x 24"'),
        factor: 1729.2,
        length: 0,
        lengthMeters: 0,
        weightPounds: 50,
        weightKilos: 22.5,
    },
    {
        shippingReel: translate('Wood 48" x 28"'),
        factor: 2464.896,
        length: 0,
        lengthMeters: 0,
        weightPounds: 100,
        weightKilos: 45,
    },
    {
        shippingReel: translate('Wood 60" x 32"'),
        factor: 5407.68,
        length: 0,
        lengthMeters: 0,
        weightPounds: 300,
        weightKilos: 135,
    },
];

const ReelCapacityCalculator: React.FC<Props> = () => {
    const [diameter, setDiameter] = React.useState<number | undefined>();
    const [reelArray, setReelArray] = React.useState<Reel[]>(initialReelArray);
    const [lengthCalculated, setLengthCalculated] = React.useState<boolean>(false);
    const handleSubmit = (event: any) => {
        event.preventDefault();
        diameter && calculateReelLength(diameter);
    };

    const handleReset = () => {
        setDiameter(undefined);
        setReelArray(initialReelArray);
        setLengthCalculated(false);
    };

    const calculateReelLength = (cableDiameter: number) => {
        if (reelArray) {
            reelArray.forEach((reel: Reel) => {
                reel.length = Math.round((reel.factor / (cableDiameter * cableDiameter)) * 100) / 100;
                reel.lengthMeters = Math.round((reel.length / 3.281) * 100) / 100;
            });
        }
        setLengthCalculated(true);
    };

    return (
        <StyledForm data-test-selector="reelCapacityCalculatorForm" onSubmit={handleSubmit} onReset={handleReset}>
            {!lengthCalculated && (
                <>
                    <TextField
                        value={diameter}
                        label={translate("Cable Diameter (inches)")}
                        required={true}
                        {...styles.diameterTextField}
                        {...{ displayStarForRequired: true, validateAs: "number" }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setDiameter(Number(event.target.value));
                        }}
                        // XNG-Changes
                        maxLength={16}
                    />
                    <Button type="submit">{translate("Calculate")}</Button>
                </>
            )}
            {!!lengthCalculated && (
                <StyledWrapper {...styles.resultsWrapper}>
                    <Typography {...styles.resultsText}>{translate("Results")}</Typography>
                    <Typography {...styles.infoText}>
                        {translate("For a cable that is")}
                        <Typography {...styles.diameterText}> {diameter} </Typography>
                        {translate(
                            "inch(es) in diameter the following table shows how many meters or feet of that cable will fit on a given reel.",
                        )}
                    </Typography>
                    <DataTable {...styles.resultsTable}>
                        <DataTableHead>
                            <DataTableHeader>{translate("Shipping Reel")}</DataTableHeader>
                            <DataTableHeader>{translate("Length of Cable (ft.)")}</DataTableHeader>
                            <DataTableHeader>{translate("Length of Cable (m)")}</DataTableHeader>
                            <DataTableHeader>{translate("Shipping Reel Weight (lbs.)*")}</DataTableHeader>
                            <DataTableHeader>{translate("Shipping Reel Weight (kg)*")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {reelArray.map((reel: Reel) => (
                                <DataTableRow key={reel.shippingReel}>
                                    <DataTableCell>{reel.shippingReel}</DataTableCell>
                                    <DataTableCell>{reel.length}</DataTableCell>
                                    <DataTableCell>{reel.lengthMeters}</DataTableCell>
                                    <DataTableCell>{reel.weightPounds}</DataTableCell>
                                    <DataTableCell>{reel.weightKilos}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                    <Typography {...styles.infoText}>
                        <em>{translate("* the weight is ONLY for the shipping reel - does not include wire/cable")}</em>
                    </Typography>
                    <Button type="reset">{translate("Reset")}</Button>
                </StyledWrapper>
            )}
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: ReelCapacityCalculator,
    definition: {
        group: XNGAGE_ELEMENTS,
    },
};

export default widgetModule;
